<template>
  <div id="site-users-page">
    <div class="flex justify-between mt-10">
      <h2 class="intro-y text-lg font-medium">
        <span v-if="site.name != null">{{ site.name }}的</span>用戶權限
      </h2>

      <router-link to="/management/sites" type="button" class="btn btn-outline-secondary w-24">返回</router-link>
    </div>

    <div v-if="checkPermission(permissions, 'system.site_enrollment.view')">

      <ul class="nav nav-tabs mt-5" role="tablist">
        <li id="user-tab" class="nav-item flex-1" role="presentation">
          <button class="nav-link w-full py-2" :class="{'active': mode === 'user'}" type="button" @click="mode = 'user'">用戶設定<template v-if="total > 0">  (共{{ total }}名)</template></button>
        </li>
        <li id="group-tab" class="nav-item flex-1" role="presentation">
          <button class="nav-link w-full py-2" :class="{'active': mode === 'group'}" type="button" @click="mode = 'group'">來自組別設定<template v-if="totalForGroup > 0">  (共{{ totalForGroup }}名)</template></button>
        </li>
      </ul>

      <div v-show="mode == 'user'" class="tab-content border-l border-r border-b mb-5">
        <div class="tab-pane leading-relaxed p-5 active ">

          <div class="col-span-12 flex flex-wrap sm:flex-nowrap items-center mb-2 sm:mb-0">
            <button class="btn btn-primary shadow-md mr-2" @click="onAddUserModal" v-if="checkPermission(permissions, 'system.site_enrollment.create')">添加用戶</button>

            <button v-if="checkPermission(permissions, 'system.site_enrollment.delete') && selectedUserIds.length < users.length" class="btn btn-secondary shadow-md ml-2" @click="selectAllItems">
              全選
            </button>

            <button v-if="checkPermission(permissions, 'system.site_enrollment.delete') && selectedUserIds.length > 0" class="btn btn-secondary shadow-md ml-2" @click="clearSelectedItems">
              清除選擇
            </button>

            <div v-if="checkPermission(permissions, 'system.site_enrollment.delete') && selectedUserIds.length > 0" class="flex justify-center items-center action ml-2">
              <button type="button" class="btn btn-primary shadow-md dropdown-toggle" @click="action = !action" @focusout="action = false">
                動作
              </button>

              <div class="relative">
                <div class="dropdown-menu w-40" :class="{'show': action}">
                  <ul class="dropdown-content">
                    <li>
                      <button type="button" class="dropdown-item" @click.stop="openDeleteModal()">
                        <font-awesome-icon icon="fa-solid fa-trash" class="fa-1x w-5 mr-2"/>
                        移除
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="hidden md:block mx-auto text-slate-500">正在顯示第 {{ total !== 0 ? from : 0 }} 至 {{ to ?? 0 }} 名用戶</div>
            <div v-if="!loading" class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
              <div class="w-56 relative text-slate-500">
                <input v-model="search" type="text" class="form-control w-56 pr-10" placeholder="搜尋" @keyup.enter="loadSiteUsers">
                <font-awesome-icon @click="loadSiteUsers" icon="fa-solid fa-magnifying-glass" class="absolute my-auto w-4 h-4 mr-3 right-0 inset-y-0" />
              </div>
            </div>
            <div v-show="loading" class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0 flex">
              <span><i data-loading-icon="oval" class="w-8 h-8"></i></span>
            </div>
          </div>

          <div v-if="!loading" class="col-span-12 flex flex-wrap sm:flex-nowrap items-center justify-end mt-5">
            <div class="flex flex-wrap sm:flex-nowrap justify-end">
              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">角色</label>
                <select id="search_role_id" v-model="searchRoleId" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option v-for="(item, key) in roles" :key="key" :value="item.role_id">{{ item.name }}</option>
                </select>
              </div>

              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">服務單位</label>
                <select id="centre_id" v-model="centreId" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option v-for="(item, key) in centers" :key="key" :value="item.centre_id">{{ item.abbrev }}</option>
                </select>
              </div>

              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">主任級或以上</label>
                <select id="is_high_level" v-model="isHighLevel" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option :value="true">是</option>
                  <option :value="false">否</option>
                </select>
              </div>

              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">啟用</label>
                <select id="is_effective" v-model="isEffective" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option :value="true">是</option>
                  <option :value="false">否</option>
                </select>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto mt-2">
            <table class="table table-striped">
              <thead>
              <tr>
                <th v-if="checkPermission(permissions, 'system.site_enrollment.delete')" class="whitespace-nowrap">選取</th>
                <th class="whitespace-nowrap"><order-button title="名稱" column="full_name" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="whitespace-nowrap">角色</th>
                <th class="whitespace-nowrap"><order-button title="顯示名稱" column="display_name" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="職員編號" column="staff_number" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="服務單位" column="centre" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="職位名稱" column="position" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="主任級或以上" column="is_high_level" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="電郵" column="email" :order-by.sync="orderBy" :order.sync="order"></order-button></th>
                <th class="text-center whitespace-nowrap">啟用</th>
                <th class="text-center whitespace-nowrap">動作</th>
              </tr>
              </thead>

              <tbody>
              <tr class="" v-for="(item, key) in users" :key="key">
                <td v-if="checkPermission(permissions, 'system.site_enrollment.delete')"><input class="form-check-input border border-slate-500" type="checkbox" @click.stop="onSelect(item)" :value="item.user_id" v-model="selectedUserIds"></td>
                <td class="">
                  <router-link :to="`/users/${item.user_id}`">{{ item.full_name ?? '-' }}</router-link>
                </td>
                <td class="">
                  {{ item.role ? item.role.name : '-' }}
                </td>
                <td class="">
                  {{ item.display_name ?? '-' }}
                </td>
                <td class="">
                  {{ item.staff_number ?? '-' }}
                </td>
                <td class="">
                  {{ item.centre !== null ? item.centre.abbrev : '-' }}
                </td>
                <td class="">
                  {{ item.position !== null ? item.position.name : '-' }}
                </td>
                <td class="">
                  {{ item.is_high_level ? '是' : '否' }}
                </td>
                <td class="">
                  {{ item.email ?? '-' }}
                </td>
                <td class="">
                  <div class="flex justify-center align-items-center mx-4">
                    <div class="mr-2">關</div>
                    <div class="form-switch">
                      <input v-model="item.is_enrolled" :disabled="!checkPermission(permissions, 'system.site_enrollment.edit')" type="checkbox" class="form-check-input" @change="toggleUser(item)">
                    </div>
                    <div class="ml-2">開</div>
                  </div>
                </td>
                <td class="w-56">
                  <div class="flex justify-center">
                    <button v-if="checkPermission(permissions, 'system.site_enrollment.delete')" class="flex items-center text-danger" @click="openDeleteModal(item)">
                      <font-awesome-icon icon="fa-regular fa-trash-can" />
                      &nbsp;移除 </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- BEGIN: Pagination -->
      <pagination-row v-if="mode == 'user'" :pages="pages" :current-page.sync="currentPage" :item-per-page.sync="itemPerPage"></pagination-row>
      <!-- END: Pagination -->

      <div v-show="mode == 'group'" class="tab-content border-l border-r border-b mb-5">
        <div class="tab-pane leading-relaxed p-5 active ">

          <div class="col-span-12 flex flex-wrap sm:flex-nowrap items-center mb-2 sm:mb-0">

            <div class="hidden md:block mx-auto text-slate-500">正在顯示第 {{ totalForGroup !== 0 ? fromForGroup : 0 }} 至 {{ toForGroup ?? 0 }} 名用戶</div>
            <div v-if="!loading" class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
              <div class="w-56 relative text-slate-500">
                <input v-model="searchForGroup" type="text" class="form-control w-56 pr-10" placeholder="搜尋" @keyup.enter="loadSiteUserGroupUsers">
                <font-awesome-icon @click="loadSiteUserGroupUsers" icon="fa-solid fa-magnifying-glass" class="absolute my-auto w-4 h-4 mr-3 right-0 inset-y-0" />
              </div>
            </div>
            <div v-show="loading" class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0 flex">
              <span><i data-loading-icon="oval" class="w-8 h-8"></i></span>
            </div>
          </div>

          <div v-if="!loading" class="col-span-12 flex flex-wrap sm:flex-nowrap items-center justify-end mt-5">
            <div class="flex flex-wrap sm:flex-nowrap justify-end">
              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">角色</label>
                <select v-model="searchRoleIdForGroup" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option v-for="(item, key) in roles" :key="key" :value="item.role_id">{{ item.name }}</option>
                </select>
              </div>

              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">服務單位</label>
                <select v-model="centreIdForGroup" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option v-for="(item, key) in centers" :key="key" :value="item.centre_id">{{ item.abbrev }}</option>
                </select>
              </div>

              <div class="mr-2 flex-basis-50 sm:flex-basis-0 sm:w-32">
                <label class="form-label text-xs">主任級或以上</label>
                <select v-model="isHighLevelForGroup" class="form-select mr-2" aria-label="請選擇">
                  <option value="">全部</option>
                  <option :value="true">是</option>
                  <option :value="false">否</option>
                </select>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto mt-2">
            <table class="table table-striped">
              <thead>
              <tr>
                <th class="whitespace-nowrap"><order-button title="名稱" column="full_name" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
                <th class="whitespace-nowrap">角色</th>
                <th class="whitespace-nowrap"><order-button title="顯示名稱" column="display_name" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="職員編號" column="staff_number" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="服務單位" column="centre" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="職位名稱" column="position" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="主任級或以上" column="is_high_level" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
                <th class="whitespace-nowrap"><order-button title="電郵" column="email" :order-by.sync="orderByForGroup" :order.sync="orderForGroup"></order-button></th>
              </tr>
              </thead>

              <tbody>
              <tr class="" v-for="(item, key) in usersForGroup" :key="key">
                <td class="">
                  <router-link :to="`/users/${item.user_id}`">{{ item.full_name ?? '-' }}</router-link>
                </td>
                <td class="">
                  <div v-for="(enrollment, groupKey) in item.userGroupEnrollments" :key="groupKey">
                    {{ enrollment.role ? enrollment.role.name : '-' }}{{ enrollment.user_group ? ( ' [來自 ' + enrollment.user_group.name + ']' ) : '-' }}
                  </div>
                </td>
                <td class="">
                  {{ item.display_name ?? '-' }}
                </td>
                <td class="">
                  {{ item.staff_number ?? '-' }}
                </td>
                <td class="">
                  {{ item.centre !== null ? item.centre.abbrev : '-' }}
                </td>
                <td class="">
                  {{ item.position !== null ? item.position.name : '-' }}
                </td>
                <td class="">
                  {{ item.is_high_level ? '是' : '否' }}
                </td>
                <td class="">
                  {{ item.email ?? '-' }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- BEGIN: Pagination -->
      <pagination-row v-if="mode == 'group'" :pages="pagesForGroup" :current-page.sync="currentPageForGroup" :item-per-page.sync="itemPerPageForGroup"></pagination-row>
      <!-- END: Pagination -->

      <!-- BEGIN: Add User Modal -->
      <div id="add-user-modal" class="modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <!-- BEGIN: Modal Header -->
            <div class="modal-header">
              <h2 class="font-medium text-base mr-auto">
                添加用戶
              </h2>
            </div>
            <!-- END: Modal Header -->
            <!-- BEGIN: Modal Body -->
            <div class="modal-body">
              <div>
                <label for="role-id" class="form-label"><span class="text-danger">*</span> 角色</label>
                <select v-model="roleId" id="role-id" class="form-select mt-2 sm:mr-2" aria-label="Default select example">
                  <option value="">--- 請選擇 ---</option>
                  <option v-for="(item, key) in roles" :key="key" :value="item.role_id">{{ item.name }}</option>
                </select>
              </div>

              <div class="mt-3" v-if="type === 'user'">
                <label class="form-label"><span class="text-danger">*</span> 選擇用戶</label>
                <span v-show="options == null"><i data-loading-icon="oval" class="w-8 h-8"></i></span>
                <multi-select v-show="options != null" v-model="newUsers" :options="options ?? []" :multiple="true" :close-on-select="false" :clear-on-select="true" :preserve-search="true" placeholder="以名搜尋" selectLabel="按下Enter以選擇" label="searchLabel" track-by="email" :preselect-first="true" :hide-selected="true" >
                  <template slot="noResult"><span class="multiselect__option">沒有結果，請輸入其他字</span></template>
                </multi-select>
                <button type="button" class="btn btn-secondary mt-2" @click="toggleSelectAll">{{ newUsers.length > 0 ? '清除選擇' : '全選'}}</button>
              </div>

            </div>
            <!-- END: Modal Body -->
            <!-- BEGIN: Modal Footer -->
            <div class="modal-footer">
              <button type="button" data-tw-dismiss="modal" class="btn btn-outline-secondary w-20 mr-1">取消</button>
              <button type="button" class="btn btn-primary w-20" @click="onAdd" :disabled="roleId === '' || (type === 'user' && newUsers.length === 0) || (type === 'group' && newGroup == null)">儲存</button>
            </div>
            <!-- END: Modal Footer -->
          </div>
        </div>
      </div>
      <!-- END: Add User Modal -->

      <!-- BEGIN: Delete Confirmation Modal -->
      <div id="delete-confirmation-modal" class="modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-body p-0">
              <div class="p-5 text-center">
                <font-awesome-icon icon="fa-regular fa-circle-xmark" class="fa-3x text-danger" />
                <div class="text-3xl mt-5">你確定嗎？</div>
                <div class="text-slate-500 mt-2">
                  你是否要移除此用戶？
                  <br>
                  此用戶會喪失在此分站的權限
                </div>
              </div>
              <div class="px-5 pb-8 text-center">
                <button type="button" data-tw-dismiss="modal" class="btn btn-outline-secondary w-24 mr-1">取消</button>
                <button type="button" class="btn btn-danger w-24" @click="onDelete">確定</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- END: Delete Confirmation Modal -->
    </div>

    <div v-else-if="mounted">
      <div class="alert alert-danger show mt-2" role="alert">沒有權限查看</div>
    </div>

  </div>
</template>

<script>
export default {
  props: ['auth', 'permissions'],
  data() {
    return {
      mounted: false,
      loading: 0,

      mode: 'user',

      site: {
        name: null
      },
      roles: [],
      centers: [],

      users: [],

      itemPerPage: 50,
      currentPage: 1,
      pages: 0,
      from: 0,
      to: 0,
      total: 0,

      centreId: '',
      searchRoleId: '',
      isHighLevel: '',
      isEffective: '',
      search: '',
      orderBy: '',
      order: '',

      removeUser: null,

      roleId: '',
      type: 'user',
      options: null,
      newUsers: [],
      groupId: '',
      newGroup: null,

      selectedUserIds: [],
      action: false,

      usersForGroup: [],

      itemPerPageForGroup: 50,
      currentPageForGroup: 1,
      pagesForGroup: 0,
      fromForGroup: 0,
      toForGroup: 0,
      totalForGroup: 0,

      centreIdForGroup: '',
      searchRoleIdForGroup: '',
      isHighLevelForGroup: '',
      searchForGroup: '',
      orderByForGroup: '',
      orderForGroup: ''
    }
  },
  watch: {
    auth(val) {
      if (val) {
        this.init()
      }
    },
    currentPage(val) {
      this.loadSiteUsers(false)
    },
    itemPerPage(val) {
      this.loadSiteUsers()
    },
    centreId() {
      this.loadSiteUsers()
    },
    searchRoleId() {
      this.loadSiteUsers()
    },
    isHighLevel() {
      this.loadSiteUsers()
    },
    isEffective() {
      this.loadSiteUsers()
    },
    search(val) {
      if (val === '') {
        this.loadSiteUsers()
      }
    },
    order() {
      this.loadSiteUsers()
    },
    currentPageForGroup(val) {
      this.loadSiteUserGroupUsers(false)
    },
    itemPerPageForGroup(val) {
      this.loadSiteUserGroupUsers()
    },
    centreIdForGroup() {
      this.loadSiteUserGroupUsers()
    },
    searchRoleIdForGroup() {
      this.loadSiteUserGroupUsers()
    },
    isHighLevelForGroup() {
      this.loadSiteUserGroupUsers()
    },
    searchForGroup(val) {
      if (val === '') {
        this.loadSiteUserGroupUsers()
      }
    },
    orderForGroup() {
      this.loadSiteUserGroupUsers()
    }
  },
  async mounted() {
    window.tailwind.svgLoader()

    if (!this.auth) {
      return
    }

    await this.init()
  },
  methods: {
    async init() {
      await this.loadSite()
      await this.loadSiteUsers()
      this.loadSiteUserGroupUsers()
      this.loadRoles()
      this.loadCenters()

      setTimeout(() => {
        this.mounted = true
      }, 500)
    },
    async loadSite() {
      this.loading++

      this.site = await this.getSite(this.$route.params.siteId)

      this.loading--
    },
    async loadRoles() {
      this.loading++

      this.roles = await this.getRoles(this.$route.params.siteId)

      this.loading--
    },
    async loadCenters() {
      this.loading++

      this.centers = await this.getCenters()

      this.loading--
    },
    async loadSiteUsers(resetCurrentPage = true) {
      this.loading++

      const response = await this.getSiteUsers(this.$route.params.siteId, this.centreId, this.searchRoleId, this.isHighLevel, this.isEffective, this.search, this.itemPerPage, this.currentPage, this.orderBy, this.order)
      this.users = response.data
      this.pages = response.meta.page.last_page
      this.from = response.meta.page.from ?? 0
      this.to = response.meta.page.to ?? 0
      this.total = response.meta.page.total

      if (resetCurrentPage) {
        this.currentPage = 1
      }

      this.selectedUserIds = []

      this.loading--
    },
    async loadSiteUserGroupUsers(resetCurrentPage = true) {
      this.loading++

      const response = await this.getSiteGroupsUsers(this.$route.params.siteId, this.centreIdForGroup, this.searchRoleIdForGroup, this.isHighLevelForGroup, this.searchForGroup, this.itemPerPageForGroup, this.currentPageForGroup, this.orderByForGroup, this.orderForGroup)
      this.usersForGroup = response.data
      this.pagesForGroup = response.meta.page.last_page
      this.fromForGroup = response.meta.page.from ?? 0
      this.toForGroup = response.meta.page.to ?? 0
      this.totalForGroup = response.meta.page.total

      if (resetCurrentPage) {
        this.currentPageForGroup = 1
      }

      this.loading--
    },
    async loadUsers() {
      this.loading++

      const users = await this.getAllUsers()

      for (let i = 0; i < users.length; i++) {
        users[i].searchLabel = this.getUserSearchLabel(users[i])
      }

      this.options = users.filter((x) => {
        for (let i = 0; i < this.users.length; i++) {
          if (JSON.stringify(this.users[i]) === JSON.stringify(x)) {
            return false
          }
        }

        return true
      })

      this.loading--
    },
    onAddUserModal() {
      if (this.options == null) {
        this.loadUsers()
      }
      this.roleId = ''
      this.newUsers = []
      this.newGroup = null
      const el = document.querySelector('#add-user-modal')
      const modal = window.tailwind.Modal.getOrCreateInstance(el)
      modal.show()

      window.tailwind.svgLoader()
    },
    async onAdd() {
      const el = document.querySelector('#add-user-modal')
      const modal = window.tailwind.Modal.getOrCreateInstance(el)
      modal.hide()

      const data = {
        role_id: this.roleId
      }

      let response = null

      if (this.type === 'user') {
        data.users = this.newUsers
        response = await this.addSiteUsers(this.$route.params.siteId, data)
      } else {
        data.group_id = this.newGroup.group_id
        response = await this.addSiteGroup(this.$route.params.siteId, data)
      }

      let option = {
        title: '成功',
        message: '已添加用戶到分站',
        type: 'success'
      }

      if (response.code === 0) {
        this.roleId = ''
        this.newUsers = []
        this.newGroup = null
      } else {
        option = {
          title: '未能添加用戶到分站',
          message: '系統錯誤，請稍後再試',
          type: 'error'
        }
      }

      this.$notify(option)
      await this.loadSiteUsers()
    },
    openDeleteModal(item) {
      this.removeUser = item

      const el = document.querySelector('#delete-confirmation-modal')
      const modal = window.tailwind.Modal.getOrCreateInstance(el)
      modal.show()
    },
    async onDelete() {
      const el = document.querySelector('#delete-confirmation-modal')
      const modal = window.tailwind.Modal.getOrCreateInstance(el)
      modal.hide()

      if (this.removeUser !== undefined && this.removeUser !== null) {
        const response = await this.deleteSiteUser(this.$route.params.siteId, this.removeUser.user_id)

        let option = {
          title: '成功',
          message: '用戶已從分站中移除',
          type: 'success'
        }

        if (response.code === 0) {
          await this.loadSiteUsers()
        } else {
          option = {
            title: '未能從分站移除用戶',
            message: response.data.message ?? '系統錯誤，請稍後再試',
            type: 'error'
          }
        }

        this.$notify(option)
      } else if (this.selectedUserIds.length > 0) {
        let option = {
          title: '成功',
          message: '用戶已從分站中移除',
          type: 'success'
        }

        for (let i = 0; i < this.selectedUserIds.length; i++) {
          const item = this.getSelectedItem(this.selectedUserIds[i])
          if (item) {
            const response = await this.deleteSiteUser(this.$route.params.siteId, item.user_id)

            if (response.code === 0) {
              console.log('Success')
            } else {
              option = {
                title: '未能從分站移除用戶',
                message: response.data.message ?? '系統錯誤，請稍後再試',
                type: 'error'
              }
              break
            }
          }
        }

        this.$notify(option)
        this.loadSiteUsers()
      }
    },
    async toggleUser(item) {
      const response = await this.toggleSiteUser(this.$route.params.siteId, item.user_id)

      let option = {
        title: '成功',
        message: '已設定用戶分站狀態',
        type: 'success'
      }

      if (response.code === 0) {
        console.log()
      } else {
        option = {
          title: '未能設定用戶分站狀態',
          message: '系統錯誤，請稍後再試',
          type: 'error'
        }
      }

      this.$notify(option)
    },
    onSelect(item) {
    },
    selectAllItems() {
      this.clearSelectedItems()
      for (let i = 0; i < this.users.length; i++) {
        this.selectedUserIds.push(this.users[i].user_id)
      }
    },
    clearSelectedItems() {
      this.selectedUserIds = []
    },
    getSelectedItem(userId) {
      const index = this.users.findIndex(e => e.user_id === userId)
      if (index > -1) {
        return this.users[index]
      }
      return null
    },
    toggleSelectAll() {
      if (this.newUsers.length > 0) {
        this.newUsers = []
      } else {
        this.newUsers = this.options ?? []
      }
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
#site-users-page {
  .tab-content {
    background-color: #FFF;

    table {
      thead {
        th:first-child, th:nth-child(3) {
          width: 120px;
        }
      }

      td {
        position: relative;

        span {
          display: inline-block;
          width: 14px;
        }
      }
    }
  }

  .action {
    .show {
      visibility: visible;
      opacity: 1;
    }

    .dropdown-menu {
      margin-top: 10px;
      left: -100px;

      button {
        width: 100%;
      }
    }
  }
}
</style>
